'use strict';

/**
 * Seria page module — handles:
 * - Tablesaw responsive price tables
 * - Body copy shorten (show more / less)
 * - Seria enquiry form (with reCAPTCHA)
 *
 * Loaded dynamically only on template_seria pages.
 */

// import Tablesaw from 'tablesaw/dist/stackonly/tablesaw.stackonly';
import '@zachleat/table-saw';
import { shorten } from './helpers/shorten';
import globals from './globals';
import {
    postJSON, isValidEmail, bindAutoExpand, getRecaptchaToken,
    slideUp, slideDown,
} from './helpers/utils';

const LANG = document.documentElement.lang || 'bg';

// ---------------------------------------------------------------------------
// Body copy shorten
// ---------------------------------------------------------------------------

function initShorten() {
    shorten('.seria_info__bodycopy', {
        showChars: 300,
        minHideChars: 10,
        ellipsesText: '...',
        moreText: 'виж още ...',
        lessText: 'скрий',
    });
}

// ---------------------------------------------------------------------------
// Seria enquiry form
// ---------------------------------------------------------------------------

function clearErrors(form) {
    form.querySelectorAll('.error').forEach((el) => el.remove());
    form.querySelectorAll('.has-error').forEach((el) => el.classList.remove('has-error'));
    form.querySelectorAll('.field_error').forEach((el) => el.classList.remove('field_error'));
}

function setFormDisabled(form, disabled) {
    form.querySelectorAll('.form-control').forEach((el) => { el.disabled = disabled; });
}

function initForm() {
    const form = document.querySelector('.js_form_seria');
    if (!form) return;

    bindAutoExpand();

    const formParent = form.closest('.form__parent');
    const url        = form.getAttribute('action');

    // CSRF
    const tokenInput     = form.querySelector('#token');
    const postTokenName  = tokenInput?.getAttribute('name') || '';
    const postTokenValue = tokenInput?.value || '';

    // Required fields
    const names   = form.querySelector('[name=names]');
    const email   = form.querySelector('[name=email]');
    const phone   = form.querySelector('[name=phone]');
    const consent = form.querySelector('[name=consent]');
    const submit  = form.querySelector('[name=submit]');

    // Clear error on focus
    form.addEventListener('focus', (event) => {
        const input = event.target.closest('input, textarea');
        if (input) input.classList.remove('field_error');
    }, true);

    form.addEventListener('change', (event) => {
        if (event.target.name === 'consent') {
            document.querySelector('.consent_wrap')?.classList.remove('field_error');
        }
    });

    form.addEventListener('submit', (event) => {
        event.preventDefault();

        console.log('submit');

        clearErrors(form);

        submit.classList.add('loading');

        // --- Client-side validation ---
        let hasError = false;

        if (consent && !consent.checked) {
            hasError = true;
            document.querySelector('.consent_wrap')?.classList.add('field_error');
        }
        if (names && names.value === '') {
            hasError = true;
            names.classList.add('field_error');
        }
        if (!email || !isValidEmail(email.value)) {
            hasError = true;
            email?.classList.add('field_error');
        }
        if (phone && phone.value === '') {
            hasError = true;
            phone.classList.add('field_error');
        }

        if (hasError) {
            setFormDisabled(form, false);
            submit.classList.remove('loading');
            form.insertAdjacentHTML('afterbegin',
                "<span class='error' style='padding: 10px 0;'>Попълнете задължителните полета!</span>");
            if (formParent) globals.goToByScroll(formParent);
            return;
        }

        // --- Build data from all form fields ---
        const data = Object.fromEntries(new FormData(form));
        data.submit = submit.value || '';

        setFormDisabled(form, true);
        submit.disabled = true;

        // --- reCAPTCHA + submit ---
        const recaptchaAction = form.dataset.recaptchaAction || 'seria_form';
        const recaptchaError  = form.dataset.recaptchaError
            || 'reCAPTCHA validation failed. Please, try again.';

        const headers = {};
        if (postTokenName) {
            headers['X-' + postTokenName] = postTokenValue;
        }

        getRecaptchaToken(recaptchaAction, LANG)
            .then((token) => {
                data.recaptcha_token = token;

                let field = form.querySelector('[name=recaptcha_token]');
                if (!field) {
                    field = document.createElement('input');
                    field.type = 'hidden';
                    field.name = 'recaptcha_token';
                    form.appendChild(field);
                }
                field.value = token;

                return postJSON(url, data, headers);
            })
            .then((json) => {
                clearErrors(form);

                if (json.error) {
                    Object.entries(json.errors).forEach(([field, msg]) => {
                        if (field === 'form') {
                            form.insertAdjacentHTML('afterbegin',
                                `<span class='error' style='padding: 10px 0;'>${msg}</span>`);
                            if (formParent) globals.goToByScroll(formParent);
                        } else {
                            const fieldEl = document.getElementById(field);
                            if (fieldEl) {
                                fieldEl.classList.add('field_error');
                                fieldEl.insertAdjacentHTML('afterend',
                                    `<span class='error'>${msg}</span>`);
                                globals.goToByScroll(fieldEl);
                            }
                        }
                    });
                } else if (json.message && formParent) {
                    slideUp(formParent, 400, () => {
                        formParent.innerHTML = json.message;
                        slideDown(formParent, 300);
                    });
                }
            })
            .catch(() => {
                form.insertAdjacentHTML('afterbegin',
                    `<span class='error'>${recaptchaError}</span>`);
                if (formParent) globals.goToByScroll(formParent);
            })
            .finally(() => {
                setFormDisabled(form, false);
                submit.disabled = false;
                submit.classList.remove('loading');
            });
    });
}

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------

export function init() {
    initShorten();
    initForm();
}
